import React, { useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Button, Drawer, Flex } from "antd"
import moment from "moment"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useDevice } from "hooks/useDevice"
import { routes } from "router/routes"
import { Icon } from "ui/Icon"
import { NewConfirmModal } from "ui/NewConfirmModal"
import BlackoutFormModal from "pagesV2/pro/Settings/Blackout/modals/BlackoutFormModal"
import { useBlackouts } from "pagesV2/pro/Settings/Blackout/hooks/useBlackouts"
import { BLACKOUT_SCOPES, SCOPE_TAG, isSameId } from "helpers/blackouts"
import { DATE_FORMAT } from "modules/datepicker/constants"
import BlackoutDayCard from "./BlackoutDayCard"
import ResourceConflictCard from "./ResourceConflictCard"
import styles from "./index.module.css"

const PANEL_WIDTH = 600
const HEADER_DATE_FORMAT = "ddd, MMMM D, YYYY"

/** Sections render in a fixed order regardless of how the feed happens to be sorted. */
const SCOPE_ORDER = [BLACKOUT_SCOPES.BUSINESS, BLACKOUT_SCOPES.LISTING, BLACKOUT_SCOPES.CREW, BLACKOUT_SCOPES.EQUIPMENT]

/**
 * Everything a single calendar day carries: its blackouts grouped by scope, any resource
 * conflicts, and a jump to the reservations the closures affect.
 *
 * Editing and deleting reuse the settings page's `useBlackouts` and `BlackoutFormModal` —
 * the calendar shows a day-resolved view, but a change still edits the underlying record.
 */
const DayDetailsPanel = ({ date, dayBlackout, isOpen, onClose, onNavigate, onChanged }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMobile } = useDevice()

  const {
    items,
    isSaving,
    isDeleting,
    formState,
    closeFormModal,
    handleSubmit,
    blackoutToDelete,
    setBlackoutToDelete,
    handleDelete,
    getMenuItems
  } = useBlackouts({ enabled: isOpen })

  const blackouts = dayBlackout?.blackouts || []
  const conflicts = dayBlackout?.conflicts || []
  const totalAffected = dayBlackout?.bookings_requiring_action || 0
  const isEmpty = !blackouts.length && !conflicts.length

  const groups = useMemo(() => {
    return SCOPE_ORDER.map((scope) => ({ scope, entries: blackouts.filter((blackout) => blackout.scope === scope) })).filter(
      (group) => group.entries.length > 0
    )
  }, [blackouts])

  /**
   * The reservations affected by this day, pre-filtered by the listings the closures hit.
   * A business closure names no listings, so it falls back to every reservation that day.
   */
  const goToReservations = useCallback(() => {
    const params = new URLSearchParams({ range: "date", date, sort: "date", status: "upcoming" })
    ;(dayBlackout?.experience_ids || []).forEach((id) => params.append("experiences[]", id))
    navigate(`${routes.guideReservationsPath()}?${params.toString()}`)
  }, [navigate, date, dayBlackout])

  const shiftDay = (offset) => onNavigate(moment(date, DATE_FORMAT).add(offset, "days").format(DATE_FORMAT))

  /**
   * The scope heading. `label` is server copy (D3) and is preferred, but the day feed does not
   * send it on every shape it has shipped — falling back to the scope's own name keeps the
   * heading from reading "undefined (2)".
   */
  const getGroupLabel = (group) => {
    const tag = SCOPE_TAG[group.scope]
    return group.entries[0].label || (tag ? t(tag.labelKey, { defaultValue: tag.labelDefault }) : group.scope)
  }

  // The menu acts on the underlying record, which the feed only references by id.
  const getCardMenuItems = (blackout) => {
    const record = items.find((item) => isSameId(item.id, blackout.id))
    return record ? getMenuItems(record) : []
  }

  const submitAndRefresh = async (payload) => {
    await handleSubmit(payload)
    onChanged?.()
  }

  const deleteAndRefresh = async () => {
    await handleDelete()
    onChanged?.()
  }

  return (
    <>
      <Drawer
        open={isOpen}
        onClose={onClose}
        placement={isMobile ? "bottom" : "right"}
        width={PANEL_WIDTH}
        height={isMobile ? "85%" : undefined}
        title={null}
        closable={false}
        extra={null}
        classNames={{ body: styles.body }}
      >
        <Flex align="center" justify="flex-end">
          <button type="button" className={styles.iconButton} onClick={onClose} aria-label={t("common.close", { defaultValue: "Close" })}>
            <Icon name="X" width={20} height={20} color="var(--color-text-primary)" />
          </button>
        </Flex>

        <Flex align="center" justify="space-between" gap="var(--spacing-m)" className={styles.dateNav}>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => shiftDay(-1)}
            aria-label={t("calendar.blackout.previous_day", { defaultValue: "Previous day" })}
          >
            <Icon name="ChevronLeft" width={20} height={20} color="var(--color-text-primary)" />
          </button>
          <span className={styles.dateTitle}>{moment(date, DATE_FORMAT).format(HEADER_DATE_FORMAT)}</span>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => shiftDay(1)}
            aria-label={t("calendar.blackout.next_day", { defaultValue: "Next day" })}
          >
            <Icon name="ChevronRight" width={20} height={20} color="var(--color-text-primary)" />
          </button>
        </Flex>

        {isEmpty ? (
          <Flex vertical align="center" justify="center" gap="var(--spacing-s)" className={styles.empty}>
            <Icon name="calendar" type="ds" width={40} height={40} color="var(--color-text-tertiary-light)" />
            <span className={styles.emptyText}>
              {t("calendar.blackout.no_issues", { defaultValue: "No availability issues for this day" })}
            </span>
          </Flex>
        ) : (
          <Flex vertical gap="var(--spacing-l)">
            {totalAffected > 0 && (
              <Flex align="center" justify="space-between" gap="var(--spacing-s)" wrap className={styles.totalBar}>
                <span className={styles.totalText}>
                  {t("calendar.blackout.total_affected", {
                    defaultValue: "Total: {{count}} bookings affected",
                    count: totalAffected
                  })}
                </span>
                <Button type="primary" shape="round" onClick={goToReservations}>
                  {t("calendar.blackout.manage_in_reservations", { defaultValue: "Manage in Reservations Page" })}
                </Button>
              </Flex>
            )}

            {groups.map((group) => (
              <Flex vertical gap="var(--spacing-s)" key={group.scope}>
                <span className={styles.sectionHeading}>{`${getGroupLabel(group)} (${group.entries.length})`}</span>
                {group.entries.map((blackout) => (
                  <BlackoutDayCard
                    key={blackout.id}
                    blackout={blackout}
                    date={date}
                    menuItems={getCardMenuItems(blackout)}
                    onReview={goToReservations}
                  />
                ))}
              </Flex>
            ))}

            {conflicts.length > 0 && (
              <Flex vertical gap="var(--spacing-s)">
                <span className={styles.sectionHeading}>
                  {t("calendar.blackout.resources_conflict", {
                    defaultValue: "Resources Conflict ({{count}})",
                    count: conflicts.length
                  })}
                </span>
                {conflicts.map((conflict) => (
                  <ResourceConflictCard key={`${conflict.type}-${conflict.detail}`} conflict={conflict} />
                ))}
              </Flex>
            )}
          </Flex>
        )}
      </Drawer>

      {formState && (
        <BlackoutFormModal
          isOpen
          scope={formState.scope}
          blackout={formState.blackout}
          onCancel={closeFormModal}
          onSubmit={submitAndRefresh}
          isSaving={isSaving}
        />
      )}

      <NewConfirmModal
        visible={!!blackoutToDelete}
        onClose={() => setBlackoutToDelete(null)}
        onConfirm={deleteAndRefresh}
        title={t("settings.blackout.delete_title", { defaultValue: "Delete Blackout?" })}
        message={t("settings.blackout.delete_message", { defaultValue: "Are you sure you want to delete this blackout?" })}
        confirmLabel={t("settings.action_delete", { defaultValue: "Delete" })}
        loading={isDeleting}
      />
    </>
  )
}

DayDetailsPanel.propTypes = {
  /** The day being shown, "YYYY-MM-DD". */
  date: PropTypes.string,
  /** That day's entry from the blackout feed, or null when the day is clear. */
  dayBlackout: PropTypes.shape({
    bookings_requiring_action: PropTypes.number,
    experience_ids: PropTypes.array,
    blackouts: PropTypes.array,
    conflicts: PropTypes.array
  }),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Moves the panel to another date, "YYYY-MM-DD". */
  onNavigate: PropTypes.func.isRequired,
  /** Called after a blackout is saved or deleted so the calendar can refetch. */
  onChanged: PropTypes.func
}

export default DayDetailsPanel
